/* eslint-disable max-lines */
import { useReviewCombobox } from '@/components/layout/Navigation/sessions/header/hooks/useReviewCombobox';
import { useSessionDeletedRecovery } from '@/components/layout/Navigation/sessions/side-nav-list/hooks/useSessionDeletedRecovery';
import { OfflineGatedTooltip } from '@/components/offline-recording/offline-gated-tooltip';
import {
  type ConsolidatedSortOption,
  consolidatedSessionsSortByAtom,
  consolidatedSessionsSortOrderAtom,
} from '@/features/consolidatedSessionsList/atoms';
import StatusFilterPanel from '@/features/consolidatedSessionsList/components/sessionSettingsPopover/statusFilterPanel';
import { consolidatedSessionsListMessages } from '@/features/consolidatedSessionsList/consolidatedSessionsList.messages';
import { useConsolidatedSessionFilters } from '@/features/consolidatedSessionsList/hooks/useConsolidatedSessionFilters';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { useMutateDeleteEmptySessions } from '@/hooks/mutation/useMutateDeleteEmptySessions';
import { useIsReviewingOtherUser } from '@/hooks/query/useQueryUser';
import { useShowSessionReview } from '@/hooks/useShowSessionReview';
import { useTidyUpRecordingLock } from '@/hooks/useTidyUpRecordingLock';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@heidi/design-system/command';
import { DateRangePickerWithPresets } from '@heidi/design-system/date-range-picker';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import { Separator } from '@heidi/design-system/separator';
import { TypographyCaptionBold, TypographyP2 } from '@heidi/design-system/typography';
import { atom, useAtom } from 'jotai';
import {
  ArrowDownUp,
  ArrowUpNarrowWide,
  ArrowUpWideNarrow,
  CalendarIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleUserRound,
  Eraser,
  LoaderIcon,
  RotateCcw,
  Settings2,
} from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const isSessionSettingsOpenAtom = atom(false);

type SettingsPanel = 'main' | 'date' | 'status' | 'patient';

const sortOptions: {
  value: ConsolidatedSortOption;
  message: (typeof consolidatedSessionsListMessages)[
    | 'dateCreated'
    | 'dateUpdated'
    | 'name'
    | 'status'];
}[] = [
  { value: 'createdAt', message: consolidatedSessionsListMessages.dateCreated },
  { value: 'updatedAt', message: consolidatedSessionsListMessages.dateUpdated },
  { value: 'name', message: consolidatedSessionsListMessages.name },
  { value: 'status', message: consolidatedSessionsListMessages.status },
];

const SessionSettingsPopover = memo(() => {
  const intl = useIntl();
  const { shouldGateControls: isOfflineGated } = useOfflineGating();
  const [isOpen, setIsOpen] = useAtom(isSessionSettingsOpenAtom);
  const [activePanel, setActivePanel] = useState<SettingsPanel>('main');
  const [sortBy, setSortBy] = useAtom(consolidatedSessionsSortByAtom);
  const [sortOrder, setSortOrder] = useAtom(consolidatedSessionsSortOrderAtom);
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const showSessionReview = useShowSessionReview();
  const isReviewingOtherUser = useIsReviewingOtherUser();
  const { mutateAsync: onDeleteEmptySessions } = useMutateDeleteEmptySessions();
  const onSessionDeleted = useSessionDeletedRecovery();
  const { isLocked: isTidyUpLocked, lockedReason: tidyUpLockedReason } =
    useTidyUpRecordingLock();

  const {
    selectedDateRangeFilter,
    setSelectedDateRangeFilter,
    patientProfiles,
    selectedPatientProfilesFilter,
    selectedStatusesFilter,
    searchValue,
    setSearchValue,
    handleFilter,
    handleStatusChange,
    handleSelectPatientProfile,
    setIsPatientFilterOpen,
    isFilterApplied,
    resetFilters,
  } = useConsolidatedSessionFilters();
  const {
    userData,
    selectedReviewUser,
    isTogetherOrHigherTier,
    isGlobalAdmin,
    onSelectReviewUser,
    userList,
  } = useReviewCombobox();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        setActivePanel('main');
        setSearchValue('');
        setIsPatientFilterOpen(false);
        setDatePickerOpen(false);
      }
    },
    [setIsOpen, setSearchValue, setIsPatientFilterOpen, setDatePickerOpen]
  );

  const openFilterDetailPanel = useCallback(
    (panel: SettingsPanel) => {
      setActivePanel(panel);
      setIsPatientFilterOpen(panel === 'patient');
    },
    [setIsPatientFilterOpen]
  );

  const onBack = useCallback(() => {
    setActivePanel('main');
    setIsPatientFilterOpen(false);
  }, [setIsPatientFilterOpen]);

  useEffect(() => {
    if (!isOfflineGated) return;
    // Route the close through the normal handler so a connectivity drop from a
    // nested panel also resets activePanel / patient search / date picker,
    // rather than leaving stale state to reappear on the next open.
    if (isOpen) handleOpenChange(false);
    // Also clear any applied filters: the gated trigger is the only way to
    // change them, so a narrowed cached list would otherwise strand the user.
    if (isFilterApplied) resetFilters();
  }, [isOfflineGated, isOpen, isFilterApplied, handleOpenChange, resetFilters]);

  const onDeleteEmptySessionClick = useCallback(async () => {
    await onDeleteEmptySessions();
    onSessionDeleted();
  }, [onDeleteEmptySessions, onSessionDeleted]);

  const panelTitle = useMemo(() => {
    switch (activePanel) {
      case 'date':
        return intl.formatMessage(consolidatedSessionsListMessages.date);
      case 'status':
        return intl.formatMessage(consolidatedSessionsListMessages.statusFilter);
      case 'patient':
        return intl.formatMessage(consolidatedSessionsListMessages.patientProfile);
      default:
        return intl.formatMessage(consolidatedSessionsListMessages.filterSessions);
    }
  }, [activePanel, intl]);

  const panelWidthClass = useMemo(() => {
    if (activePanel === 'date') {
      return 'w-max';
    }
    return 'w-[280px]';
  }, [activePanel]);

  const settingsTrigger = (
    <Button
      data-testid="session-list-filter-button"
      variant="panel_ghost"
      size="xxs"
      iconOnly
      disableAutomaticSvgSizing
      disabled={isOfflineGated}
      data-active={isOpen || isFilterApplied}
      className="text-text-secondary"
      aria-label={intl.formatMessage(consolidatedSessionsListMessages.filterSessions)}
    >
      <Settings2 className="size-4 shrink-0" aria-hidden="true" />
    </Button>
  );

  // Offline: gate the whole popover trigger per the offline read-only policy
  // (see useOfflineGating). "View sessions as" and "Tidy sessions" are genuinely
  // server-bound; the status/date/patient filters and sort run client-side over
  // the cached list but are greyed for the consistent read-only-offline
  // treatment rather than out of technical necessity.
  if (isOfflineGated) {
    return <OfflineGatedTooltip isOfflineGated>{settingsTrigger}</OfflineGatedTooltip>;
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{settingsTrigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={8}
        className={cn(panelWidthClass, 'p-0')}
      >
        {activePanel !== 'main' && (
          <>
            <div className="flex items-center gap-1 p-2">
              <Button
                iconOnly
                size="xxs"
                variant="ghost"
                onClick={onBack}
                aria-label={intl.formatMessage(consolidatedSessionsListMessages.goBack)}
              >
                <ChevronLeft className="size-4 rtl:-scale-x-100" />
              </Button>
              <TypographyCaptionBold>{panelTitle}</TypographyCaptionBold>
            </div>
            <Separator />
          </>
        )}

        {activePanel === 'main' && (
          <div className="flex flex-col gap-2 p-3">
            {showSessionReview && (
              <>
                <p className="text-xs font-medium text-text-secondary">
                  <FormattedMessage
                    {...consolidatedSessionsListMessages.viewSessionsAs}
                  />
                </p>
                <Select value={selectedReviewUser} onValueChange={onSelectReviewUser}>
                  <SelectTrigger
                    className="h-8 bg-surface"
                    icon={<ChevronDown className="size-4 text-text-secondary" />}
                  >
                    <SelectValue
                      placeholder={intl.formatMessage(
                        consolidatedSessionsListMessages.me
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {userList?.map((user) => (
                      <SelectItem
                        key={user.kinde_user_id}
                        value={user.kinde_user_id}
                        disabled={
                          (!isTogetherOrHigherTier || isGlobalAdmin) &&
                          user.kinde_user_id !== userData?.kinde_user_id
                        }
                      >
                        {user.kinde_user_id === userData?.kinde_user_id
                          ? intl.formatMessage(consolidatedSessionsListMessages.me)
                          : user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Separator />
              </>
            )}

            <div className="flex flex-col">
              <TypographyCaptionBold className="text-text-tertiary">
                <FormattedMessage {...consolidatedSessionsListMessages.filter} />
              </TypographyCaptionBold>
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-sm px-1 py-2 text-left hover:bg-background-secondary"
                onClick={() => openFilterDetailPanel('status')}
              >
                <div className="flex items-center gap-2">
                  <LoaderIcon className="size-4 text-text-secondary" />
                  <TypographyP2>
                    <FormattedMessage
                      {...consolidatedSessionsListMessages.statusFilter}
                    />
                  </TypographyP2>
                </div>
                <ChevronRight className="size-4 text-text-secondary rtl:-scale-x-100" />
              </button>
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-sm px-1 py-2 text-left hover:bg-background-secondary"
                onClick={() => {
                  setDatePickerOpen(true);
                  openFilterDetailPanel('date');
                }}
              >
                <div className="flex items-center gap-2">
                  <CalendarIcon className="size-4 text-text-secondary" />
                  <TypographyP2>
                    <FormattedMessage {...consolidatedSessionsListMessages.date} />
                  </TypographyP2>
                </div>
                <ChevronRight className="size-4 text-text-secondary rtl:-scale-x-100" />
              </button>
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-sm px-1 py-2 text-left hover:bg-background-secondary"
                onClick={() => openFilterDetailPanel('patient')}
              >
                <div className="flex items-center gap-2">
                  <CircleUserRound className="size-4 text-text-secondary" />
                  <TypographyP2>
                    <FormattedMessage
                      {...consolidatedSessionsListMessages.patientProfile}
                    />
                  </TypographyP2>
                </div>
                <ChevronRight className="size-4 text-text-secondary rtl:-scale-x-100" />
              </button>
            </div>

            <div className="flex flex-col gap-0.5">
              <TypographyCaptionBold className="text-text-tertiary">
                <FormattedMessage {...consolidatedSessionsListMessages.display} />
              </TypographyCaptionBold>
              <div className="flex items-center gap-2 px-1">
                <ArrowDownUp className="size-3.5 shrink-0" />
                <TypographyP2 className="shrink-0">
                  <FormattedMessage {...consolidatedSessionsListMessages.orderBy} />
                </TypographyP2>
                <Select
                  value={sortBy}
                  onValueChange={(v) => setSortBy(v as ConsolidatedSortOption)}
                >
                  <SelectTrigger
                    className="h-7 flex-1 bg-surface"
                    icon={<ChevronDown className="size-3 text-text-secondary" />}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {intl.formatMessage(option.message)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="xs"
                  iconOnly
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  aria-label={
                    sortOrder === 'asc'
                      ? intl.formatMessage(consolidatedSessionsListMessages.descending)
                      : intl.formatMessage(consolidatedSessionsListMessages.ascending)
                  }
                >
                  {sortOrder === 'asc' ? (
                    <ArrowUpNarrowWide className="size-4 shrink-0" />
                  ) : (
                    <ArrowUpWideNarrow className="size-4 shrink-0" />
                  )}
                </Button>
              </div>
            </div>

            {!isReviewingOtherUser && (
              <OfflineGatedTooltip
                isOfflineGated={isTidyUpLocked}
                className="w-full"
                content={tidyUpLockedReason}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  className="w-full gap-2 text-text-secondary"
                  disabled={isTidyUpLocked}
                  onClick={() => void onDeleteEmptySessionClick()}
                >
                  <Eraser className="size-3" />
                  <TypographyCaptionBold>
                    <FormattedMessage
                      {...consolidatedSessionsListMessages.tidySessions}
                    />
                  </TypographyCaptionBold>
                </Button>
              </OfflineGatedTooltip>
            )}

            <Separator />

            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="xxs"
                className="w-auto justify-start gap-2 text-text-secondary disabled:opacity-50"
                onClick={resetFilters}
                disabled={!isFilterApplied}
              >
                <RotateCcw className="size-3" />
                <TypographyCaptionBold>
                  <FormattedMessage {...consolidatedSessionsListMessages.clearFilters} />
                </TypographyCaptionBold>
              </Button>
            </div>
          </div>
        )}

        {activePanel === 'date' && (
          <DateRangePickerWithPresets
            range={selectedDateRangeFilter}
            setRange={setSelectedDateRangeFilter}
            isOpen={isDatePickerOpen}
            setIsOpen={setDatePickerOpen}
          />
        )}

        {activePanel === 'status' && (
          <StatusFilterPanel
            selectedStatusesFilter={selectedStatusesFilter}
            handleStatusChange={handleStatusChange}
          />
        )}

        {activePanel === 'patient' && (
          <Command filter={handleFilter}>
            <CommandInput
              value={searchValue}
              onValueChange={setSearchValue}
              placeholder={intl.formatMessage(
                consolidatedSessionsListMessages.searchForPatient
              )}
              className="h-9 py-0"
            />
            <CommandList className="max-h-[320px]">
              <CommandEmpty>
                <FormattedMessage {...consolidatedSessionsListMessages.noPatientFound} />
              </CommandEmpty>
              <CommandGroup>
                {patientProfiles?.map((patientProfile) => (
                  <CommandItem
                    key={patientProfile.id}
                    onSelect={() => {
                      if (patientProfile.id) {
                        handleSelectPatientProfile(patientProfile.id);
                      }
                    }}
                    value={patientProfile.id ?? ''}
                    className="flex items-center gap-2"
                  >
                    <Checkbox
                      checked={
                        !!patientProfile.id &&
                        selectedPatientProfilesFilter.includes(patientProfile.id)
                      }
                    />
                    <TypographyP2 className="truncate">
                      {patientProfile.demographic_string}
                    </TypographyP2>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
});

SessionSettingsPopover.displayName = 'SessionSettingsPopover';
export default SessionSettingsPopover;
